"use client";

import { Clock, Mail, MapPin, Newspaper, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5">
        <p className="font-default text-sm uppercase tracking-widest text-[color:var(--accent-foreground)] mb-3">
          — Consulta recibida
        </p>
        <p className="font-serif text-2xl text-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-foreground/30 text-foreground hover:bg-foreground hover:text-background h-12 px-6 font-default uppercase tracking-widest text-xs">
          Enviar otra consulta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-1.5 h-1.5 rounded-lg bg-[color:var(--accent-foreground)]" />
        <span className="font-default text-[11px] uppercase tracking-[0.25em] text-[color:var(--accent-foreground)] font-medium">
          Solicitar primera consulta
        </span>
      </div>

      <FormField control={control} name="name" rules={{
      required: "El nombre es obligatorio",
      minLength: {
        value: 2,
        message: "Mínimo 2 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
              Nombre completo
            </FormLabel>
            <FormControl>
              <Input placeholder="Su nombre" {...field} className="h-14 bg-transparent border-foreground/20 border-x-0 border-t-0 border-b px-0 text-foreground placeholder:text-muted-foreground/60 font-serif text-lg focus-visible:border-[color:var(--accent-foreground)] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "El email es obligatorio",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Email no válido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
              Correo electrónico
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="nombre@ejemplo.com" {...field} className="h-14 bg-transparent border-foreground/20 border-x-0 border-t-0 border-b px-0 text-foreground placeholder:text-muted-foreground/60 font-serif text-lg focus-visible:border-[color:var(--accent-foreground)] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
              Teléfono
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+34 000 000 000" {...field} className="h-14 bg-transparent border-foreground/20 border-x-0 border-t-0 border-b px-0 text-foreground placeholder:text-muted-foreground/60 font-serif text-lg focus-visible:border-[color:var(--accent-foreground)] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Seleccione un motivo"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
              Motivo de consulta
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-14 w-full bg-transparent border-foreground/20 border-x-0 border-t-0 border-b px-0 text-foreground font-serif text-lg data-[placeholder]:text-muted-foreground/60 focus-visible:border-[color:var(--accent-foreground)] focus-visible:ring-0">
                  <SelectValue placeholder="Seleccione una opción" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="Primera consulta" className="font-serif">
                  Primera consulta
                </SelectItem>
                <SelectItem value="Información general" className="font-serif">
                  Información general
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "El mensaje es obligatorio",
      minLength: {
        value: 10,
        message: "Por favor, detalle un poco más"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
              Detalles
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Cuénteme brevemente el motivo de su consulta..." {...field} className="min-h-[200px] bg-transparent border-foreground/20 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 font-serif text-lg focus-visible:border-[color:var(--accent-foreground)] focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "Debe aceptar la política de privacidad"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3 pt-2">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-foreground/40 data-[state=checked]:bg-[color:var(--accent-foreground)] data-[state=checked]:border-[color:var(--accent-foreground)]" />
              </FormControl>
              <Label htmlFor={field.name} className="font-serif text-sm text-muted-foreground leading-relaxed cursor-pointer">
                He leído y acepto la política de privacidad y el tratamiento de mis datos.
              </Label>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive font-default text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-background font-default uppercase tracking-[0.25em] text-xs font-semibold">
          {isSubmitting ? "Enviando..." : "Enviar consulta →"}
        </Button>
        <p className="font-serif italic text-[13px] text-muted-foreground text-center">
          — Responderé en menos de 24 horas.
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const infoBlocks = [{
    icon: MapPin,
    label: "Ubicación",
    lines: ["Calle de Viriato, 57 - 28010 Madrid", "Metro Iglesia (Línea 1)"],
    link: {
      href: "https://maps.app.goo.gl/Gw4SJcmjr16atWY79",
      newTab: true
    }
  }, {
    icon: Mail,
    label: "Email",
    lines: ["antonio.heredia@cop.es"],
    isEmail: true
  }, {
    icon: Phone,
    label: "Teléfono",
    lines: ["+34 684 728 504"],
    link: {
      href: "tel:+34684728504",
      newTab: false
    }
  }, {
    icon: Clock,
    label: "Horario",
    lines: ["Atención con cita previa"]
  }];
  return <section data-section-id="5240" id="formulario-de-consulta" className="bg-background text-foreground relative overflow-hidden py-32 md:py-40 lg:py-[160px] lg:pt-[180px] px-6 md:px-10 lg:px-16">
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-lg bg-[color:var(--accent-foreground)]/5 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-lg bg-[color:var(--accent-foreground)]/5 blur-3xl" />
      </div>

      <div className="relative max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-12 xl:gap-16">
          <div ref={leftRef} className={`xl:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-2 mb-12">
              <span className="inline-block w-2 h-2 rounded-lg bg-[color:var(--accent-foreground)]" />
              <span className="font-default text-[11px] uppercase tracking-[0.3em] text-muted-foreground font-medium">
                Contacto
              </span>
            </div>

            <h1 className="font-default font-extrabold leading-[0.88] tracking-tighter text-foreground mb-10 md:mb-12">
              <span className="block text-[clamp(48px,8vw,110px)]">SOLICITAR</span>
              <span className="block text-[clamp(48px,8vw,110px)]">
                CONSULTA{" "}
                <span className="font-serif italic font-normal text-[color:var(--accent-foreground)] inline-block lg:ml-12">
                  CLÍNICA.
                </span>
              </span>
            </h1>

            <p className="font-serif italic text-xl md:text-[22px] leading-relaxed text-muted-foreground max-w-[420px] mb-16 md:mb-14">Si tienes alguna duda o quieres pedir una primera cita, puedes ponerte en contacto conmigo de la forma que te resulte más cómoda.</p>

            <div className="space-y-0 max-w-[420px]">
              {infoBlocks.map((block, i) => {
              const Icon = block.icon;
              return <div data-index={i} key={i} className="border-b border-border/30 last:border-0">
                    <Link className="py-8 md:py-10 flex items-start gap-4 no-underline cursor-pointer" {...block.link}><div>
                      <Icon className="size-4 text-[color:var(--accent-foreground)] mt-1 shrink-0" />
                      <div className="flex-1">
                        <p className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium mb-2">
                          {block.label}
                        </p>
                        {block.isEmail ? <Link to="mailto:antonio.heredia@cop.es" className="font-serif text-lg text-foreground hover:text-[color:var(--accent-foreground)] underline underline-offset-[6px] decoration-[color:var(--accent-foreground)] decoration-1 transition-colors">
                            {block.lines[0]}
                          </Link> : <div>
                            {block.lines.map((line, j) => <p key={j} className="font-serif text-lg text-foreground leading-snug">
                                {line}
                              </p>)}
                          </div>}
                      </div>
                    </div></Link>
                  </div>;
            })}
            </div>
          </div>

          <div ref={rightRef} className={`xl:col-span-6 xl:col-start-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="formulario-de-consulta" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: "",
            consent: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Enviar consulta" successMessage="Gracias por su mensaje. Me pondré en contacto con usted en menos de 24 horas." errorMessage="Ha ocurrido un error. Por favor, inténtelo de nuevo o envíeme un email directamente." className="border border-border/20 bg-foreground/[0.03] backdrop-blur-xl p-10 md:p-14 shadow-lg rounded-lg">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}